import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';

export default function MultiSelect({ 
  options, 
  selected, 
  onChange, 
  placeholder = "Select options...",
  showAllOption = false,
  allOptionLabel = "All"
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = (value) => {
    if (showAllOption && value === '__ALL__') {
      // Toggle all
      if (selected.length === options.length) {
        onChange([]);
      } else {
        onChange(options.map(opt => opt.value));
      }
    } else {
      // Toggle individual option
      if (selected.includes(value)) {
        onChange(selected.filter(v => v !== value));
      } else {
        onChange([...selected, value]);
      }
    }
  };

  const handleRemove = (value, e) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  };

  const isAllSelected = showAllOption && selected.length === options.length;

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[42px] flex items-center flex-wrap gap-1"
      >
        {selected.length === 0 ? (
          <span className="text-gray-500">{placeholder}</span>
        ) : (
          <>
            {selected.slice(0, 3).map((value) => {
              const option = options.find(opt => opt.value === value);
              return (
                <span
                  key={value}
                  className="inline-flex items-center gap-1 bg-purple-100 text-purple-800 px-2 py-1 rounded text-sm"
                >
                  {option?.label || value}
                  <button
                    type="button"
                    onClick={(e) => handleRemove(value, e)}
                    className="hover:bg-purple-200 rounded"
                  >
                    <X size={14} />
                  </button>
                </span>
              );
            })}
            {selected.length > 3 && (
              <span className="text-gray-600 text-sm">+{selected.length - 3} more</span>
            )}
          </>
        )}
        <ChevronDown size={20} className="ml-auto text-gray-400" />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {showAllOption && (
            <div
              onClick={() => handleToggle('__ALL__')}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                isAllSelected ? 'bg-purple-50' : ''
              }`}
            >
              <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                isAllSelected ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
              }`}>
                {isAllSelected && <Check size={12} className="text-white" />}
              </div>
              <span className={isAllSelected ? 'font-semibold text-purple-600' : ''}>
                {allOptionLabel}
              </span>
            </div>
          )}
          {showAllOption && <div className="border-t border-gray-200 my-1"></div>}
          {options.map((option) => {
            const isSelected = selected.includes(option.value);
            return (
              <div
                key={option.value}
                onClick={() => handleToggle(option.value)}
                className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                  isSelected ? 'bg-purple-50' : ''
                }`}
              >
                <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                  isSelected ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
                }`}>
                  {isSelected && <Check size={12} className="text-white" />}
                </div>
                <span className={isSelected ? 'font-semibold text-purple-600' : ''}>
                  {option.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


